import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Trophy, Clock, Ticket, Minus, Plus, ArrowLeft, Loader2, AlertCircle, Timer } from 'lucide-react';
import { Progress } from '../components/ui/progress';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RAFFLE_IMAGES = [
  'https://images.unsplash.com/photo-1672024110512-f7028b49db28?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBzcG9ydHMlMjBjYXIlMjBkYXJrfGVufDB8fHx8MTc3NTY0NzUyNHww&ixlib=rb-4.1.0&q=85',
  'https://images.pexels.com/photos/94272/sports-car-pkw-auto-vehicle-94272.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
  'https://images.unsplash.com/photo-1707312900236-12d6fefd2bbb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxnYW1pbmclMjBzZXR1cCUyMGRhcmt8ZW58MHx8fHwxNzc1NjQ3NTI0fDA&ixlib=rb-4.1.0&q=85'
];

function calcTimeLeft(drawDate) {
  const diff = new Date(drawDate) - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, expired: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff % 86400000) / 3600000),
    minutes: Math.floor((diff % 3600000) / 60000),
    seconds: Math.floor((diff % 60000) / 1000),
    expired: false
  };
}

function CountdownBlock({ value, label }) {
  const prev = useRef(value);
  const [flip, setFlip] = useState(false);
  useEffect(() => {
    if (prev.current !== value) { setFlip(true); const t = setTimeout(() => setFlip(false), 300); prev.current = value; return () => clearTimeout(t); }
  }, [value]);
  return (
    <div className="flex flex-col items-center">
      <div className={`bg-gray-900 text-white rounded-xl w-16 h-14 flex items-center justify-center text-2xl font-bold font-mono shadow-md relative overflow-hidden transition-transform duration-300 ${flip ? 'scale-95' : 'scale-100'}`}>
        <span className={`transition-all duration-300 ${flip ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'}`}>{String(value).padStart(2, '0')}</span>
        <div className="absolute inset-x-0 top-1/2 h-px bg-white/10" />
      </div>
      <span className="text-xs uppercase tracking-wider text-gray-500 mt-1.5 font-medium">{label}</span>
    </div>
  );
}

function DetailCountdown({ drawDate }) {
  const [timeLeft, setTimeLeft] = useState(() => calcTimeLeft(drawDate));
  useEffect(() => { const id = setInterval(() => setTimeLeft(calcTimeLeft(drawDate)), 1000); return () => clearInterval(id); }, [drawDate]);
  if (timeLeft.expired) return (
    <div className="flex items-center gap-2 text-[#EAB308] font-semibold" data-testid="detail-countdown-expired">
      <Timer className="w-5 h-5" />
      <span>Draw time!</span>
    </div>
  );
  return (
    <div className="flex items-center gap-2" data-testid="detail-countdown-timer">
      <CountdownBlock value={timeLeft.days} label="Days" />
      <span className="text-gray-400 font-bold text-lg mt-[-16px]">:</span>
      <CountdownBlock value={timeLeft.hours} label="Hrs" />
      <span className="text-gray-400 font-bold text-lg mt-[-16px]">:</span>
      <CountdownBlock value={timeLeft.minutes} label="Min" />
      <span className="text-gray-400 font-bold text-lg mt-[-16px]">:</span>
      <CountdownBlock value={timeLeft.seconds} label="Sec" />
    </div>
  );
}

export default function RaffleDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [raffle, setRaffle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [reserving, setReserving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchRaffle();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRaffle = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/raffles/${id}`);
      setRaffle(response.data);
    } catch (error) {
      console.error('Error fetching raffle:', error);
      setError('Free Draw not found');
    } finally {
      setLoading(false);
    }
  };

  const handleReserve = async () => {
    if (!user) {
      navigate('/login', { state: { from: `/raffle/${id}` } });
      return;
    }

    setReserving(true);
    setError('');

    try {
      const response = await axios.post(
        `${API_URL}/api/tickets/reserve`,
        { raffle_id: id, quantity }
      );
      
      navigate('/checkout', { 
        state: { 
          reservation: response.data,
          raffle: raffle
        } 
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reserve tickets');
    } finally {
      setReserving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#EAB308] animate-spin" />
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl text-gray-900 mb-2">Free Draw not found</h2>
          <Link to="/" className="text-[#EAB308] hover:underline">Go back home</Link>
        </div>
      </div>
    );
  }

  const progress = (raffle.tickets_sold / raffle.total_tickets) * 100;
  const remaining = raffle.total_tickets - raffle.tickets_sold;
  const drawDate = new Date(raffle.draw_date);
  const total = quantity * raffle.ticket_price;
  const imageUrl = raffle.image_url || RAFFLE_IMAGES[0];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors" data-testid="back-button">
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </Link>
            <Link to="/" className="flex items-center gap-2 mx-auto" data-testid="header-logo">
              <img src="/logo.png" alt="Boss Win" className="h-20 w-auto" />
              
            </Link>
            <div className="w-20" />
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-32 md:pb-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: Image */}
          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            <img 
              src={imageUrl} 
              alt={raffle.title}
              className="w-full h-[300px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white/50 via-transparent to-transparent" />
            
            {/* Status Badge */}
            <div className="absolute top-4 left-4">
              {raffle.status === 'active' ? (
                <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                  Active
                </span>
              ) : raffle.status === 'drawn' ? (
                <span className="bg-[#EAB308] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                  Winner: #{raffle.winner_ticket}
                </span>
              ) : (
                <span className="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                  Closed
                </span>
              )}
            </div>
          </div>

          {/* Right: Details */}
          <div className="space-y-6">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-black text-gray-900 mb-4" data-testid="raffle-title">
                {raffle.title}
              </h1>
              {raffle.status === 'active' ? (
                <div className="flex items-center gap-3">
                  <Timer className="w-5 h-5 text-[#EAB308]" />
                  <span className="text-gray-500 text-sm font-medium mr-1">Draw in:</span>
                  <DetailCountdown drawDate={raffle.draw_date} />
                </div>
              ) : (
                <div className="flex items-center gap-2 text-gray-500">
                  <Clock className="w-5 h-5" />
                  <span>Draw: {drawDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                </div>
              )}
            </div>

            {/* Prize Amount */}
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-2xl p-6">
              <p className="text-gray-600 text-sm uppercase tracking-wider mb-2">Prize</p>
              <p className="text-[#EAB308] font-heading text-4xl font-black" data-testid="prize-amount">
                £{raffle.prize_amount.toLocaleString()}
              </p>
            </div>

            {/* Ticket Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Ticket Price</span>
                <span className="text-gray-900 font-bold text-xl" data-testid="ticket-price">£{raffle.ticket_price}</span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Tickets Remaining</span>
                <span className="text-[#EAB308] font-bold text-xl" data-testid="tickets-remaining">{remaining}</span>
              </div>

              {/* Progress Bar */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-gray-500">Sold</span>
                  <span className="text-gray-900">{raffle.tickets_sold} / {raffle.total_tickets}</span>
                </div>
                <Progress value={progress} className="h-3 bg-gray-200" />
              </div>
            </div>

            {/* Quantity Selector - Only show if active */}
            {raffle.status === 'active' && (
              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6">
                <p className="text-gray-600 text-sm uppercase tracking-wider mb-4">Select Quantity</p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-900 hover:border-gray-300 transition-colors"
                      disabled={quantity <= 1}
                      data-testid="decrease-quantity"
                    >
                      <Minus className="w-5 h-5" />
                    </button>
                    
                    <span className="text-gray-900 font-bold text-2xl w-16 text-center" data-testid="quantity-display">
                      {quantity}
                    </span>
                    
                    <button
                      onClick={() => setQuantity(Math.min(remaining, quantity + 1))}
                      className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center text-gray-900 hover:border-gray-300 transition-colors"
                      disabled={quantity >= remaining}
                      data-testid="increase-quantity"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="text-right">
                    <p className="text-gray-500 text-sm">Total</p>
                    <p className="text-gray-900 font-bold text-2xl" data-testid="total-price">£{total.toFixed(2)}</p>
                  </div>
                </div>

                {/* Quick Select */}
                <div className="flex gap-2 mt-4">
                  {[1, 5, 10, 20].map(num => (
                    <button
                      key={num}
                      onClick={() => setQuantity(Math.min(remaining, num))}
                      className={`flex-1 py-2 rounded-lg font-medium transition-colors ${
                        quantity === num 
                          ? 'bg-[#EAB308] text-white' 
                          : 'bg-white text-gray-900 border border-gray-200 hover:border-gray-300'
                      }`}
                      disabled={num > remaining}
                      data-testid={`quick-select-${num}`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                {error}
              </div>
            )}

            {/* Reserve Button - Desktop */}
            {raffle.status === 'active' && (
              <button
                onClick={handleReserve}
                disabled={reserving || remaining === 0}
                className="hidden md:flex w-full bg-[#EAB308] text-white font-bold py-4 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed items-center justify-center gap-2 text-lg shadow-lg shadow-[#EAB308]/25"
                data-testid="reserve-button"
              >
                {reserving ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Reserving...
                  </>
                ) : (
                  <>
                    <Ticket className="w-5 h-5" />
                    Reserve {quantity} Ticket{quantity > 1 ? 's' : ''} - £{total.toFixed(2)}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </main>

      {/* Mobile Bottom CTA */}
      {raffle.status === 'active' && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-white/90 backdrop-blur-xl border-t border-gray-200 z-50 md:hidden safe-bottom">
          <button
            onClick={handleReserve}
            disabled={reserving || remaining === 0}
            className="w-full bg-[#EAB308] text-white font-bold py-4 rounded-xl hover:bg-[#CA8A04] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-lg shadow-lg"
            data-testid="mobile-reserve-button"
          >
            {reserving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Reserving...
              </>
            ) : (
              <>
                <Ticket className="w-5 h-5" />
                Reserve {quantity} - £{total.toFixed(2)}
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
}
